import { supabaseGestionPension as supabase } from '../config/settings/supabase-client'
import { formatDateTime } from '../config/resources/utils'
import { getLogger } from '../core/debuggers'

const logger = getLogger('userOps.js')

const asArray = (data) => (Array.isArray(data) ? data : data ? [data] : [])

export async function getUserOpsAccount(userId) {
  if (!userId) return null

  const { data, error } = await supabase.rpc('get_user_ops_account', {
    p_user_id: userId,
  })

  if (error) {
    logger.error('getUserOpsAccount', error)
    throw error
  }

  const row = asArray(data)[0]
  if (!row) return null

  return {
    lastSignInAt: row.last_sign_in_at || null,
    authCreatedAt: row.auth_created_at || null,
    bannedUntil: row.banned_until || null,
    providers: row.providers || [],
    lastSeenAt: row.last_seen_at || null,
  }
}

export async function getUserOpsSessions(userId) {
  if (!userId) return []

  const { data, error } = await supabase.rpc('get_user_ops_sessions', {
    p_user_id: userId,
  })

  if (error) {
    logger.error('getUserOpsSessions', error)
    throw error
  }

  return asArray(data).map((row) => ({
    id: row.id,
    createdAt: row.created_at,
    refreshedAt: row.refreshed_at,
    lastActiveAt: row.last_active_at,
    ip: row.ip || null,
    userAgent: row.user_agent || null,
  }))
}

export async function getUserOpsAuthEvents(userId, limit = 12) {
  if (!userId) return []

  const { data, error } = await supabase.rpc('get_user_ops_auth_events', {
    p_user_id: userId,
    p_limit: limit,
  })

  if (error) {
    logger.error('getUserOpsAuthEvents', error)
    throw error
  }

  return asArray(data).map((row) => ({
    id: row.id,
    createdAt: row.created_at,
    action: row.action,
    ip: row.ip_address || null,
    provider: row.provider || null,
  }))
}

export async function revokeUserOpsSession(sessionId) {
  if (!sessionId) throw new Error('sessionId is required')

  const { data, error } = await supabase.rpc('revoke_user_ops_session', {
    p_session_id: sessionId,
  })

  if (error) {
    logger.error('revokeUserOpsSession', error)
    throw error
  }

  return Boolean(data)
}

export async function getUserConfigHistory(userId) {
  if (!userId) return []

  const selectWithActor = `
      id,
      change_type,
      created_at,
      before,
      after,
      changed_by,
      actor:profiles!pension_config_history_changed_by_fkey (
        full_name,
        email
      )
    `

  let { data, error } = await supabase
    .from('pension_config_history')
    .select(selectWithActor)
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(20)

  if (error) {
    const fallback = await supabase
      .from('pension_config_history')
      .select('id, change_type, created_at, before, after, changed_by')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(20)
    data = fallback.data
    error = fallback.error
  }

  if (error) {
    logger.error('getUserConfigHistory', error)
    throw error
  }

  return (data || []).map((row) => ({
    id: row.id,
    changeType: row.change_type,
    createdAt: row.created_at,
    before: row.before,
    after: row.after,
    changedByName: row.actor?.full_name || row.actor?.email || null,
  }))
}

export async function getUserAuditEvents(userId) {
  if (!userId) return []

  let { data, error } = await supabase
    .from('admin_audit_events')
    .select(
      `
      id,
      action,
      entity,
      created_at,
      before,
      after,
      actor:profiles!admin_audit_events_actor_id_fkey (
        full_name,
        email
      )
    `
    )
    .eq('target_user_id', userId)
    .order('created_at', { ascending: false })
    .limit(30)

  if (error) {
    const fallback = await supabase
      .from('admin_audit_events')
      .select('id, action, entity, created_at, before, after, actor_id')
      .eq('target_user_id', userId)
      .order('created_at', { ascending: false })
      .limit(30)
    data = fallback.data
    error = fallback.error
  }

  if (error) {
    logger.error('getUserAuditEvents', error)
    throw error
  }

  return (data || []).map((row) => ({
    id: row.id,
    action: row.action,
    entity: row.entity,
    createdAt: row.created_at,
    before: row.before,
    after: row.after,
    actorName: row.actor?.full_name || row.actor?.email || null,
  }))
}

export async function getUserOpsNotifications(userId) {
  if (!userId) return []

  const { data, error } = await supabase
    .from('notifications')
    .select('id, type, title, message, status, email_status, created_at, read_at, metadata')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(15)

  if (error) {
    logger.error('getUserOpsNotifications', error)
    throw error
  }

  return data || []
}

export async function getUserOpsTickets(userId) {
  if (!userId) return []

  const { data, error } = await supabase
    .from('support_tickets')
    .select('id, tracking_code, category, message, status, created_at')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(15)

  if (error) {
    logger.error('getUserOpsTickets', error)
    throw error
  }

  return data || []
}

export async function getUserOpsLogs({ userId, name }) {
  if (!userId) return []

  const { data, error } = await supabase
    .from('logs')
    .select('id, created_at, level, context, message, metadata')
    .in('context', ['SENDER', 'SCRAPER'])
    .order('created_at', { ascending: false })
    .limit(200)

  if (error) {
    logger.error('getUserOpsLogs', error)
    throw error
  }

  const needle = (name || '').trim().toLowerCase()

  return (data || [])
    .filter((row) => {
      const meta = row.metadata && typeof row.metadata === 'object' ? row.metadata : {}
      const metaId = meta.user_id || meta.userId
      if (metaId && String(metaId) === String(userId)) return true
      if (needle && String(row.message || '').toLowerCase().includes(needle)) {
        return true
      }
      return false
    })
    .slice(0, 12)
    .map((row) => ({
      id: row.id,
      createdAt: formatDateTime(row.created_at),
      createdAtRaw: row.created_at,
      level: row.level,
      context: row.context,
      message: row.message,
    }))
}
